function renderNestedFieldLayoutTemplate({
  heading,
  indent,
  columnsGrid,
  groupFields
}: {
  heading: string
  indent: string
  columnsGrid: string
  groupFields: string
}): string {
  return `${heading}${indent}<div className="grid ${columnsGrid} gap-2">
${groupFields}
${indent}</div>`
}

function renderNestedFieldDynamicListFieldTemplate({
  indent,
  pathExpression,
  nestedLabel,
  hideLabelProp,
  descriptionProp,
  fieldMaxItemsContent
}: {
  indent: string
  pathExpression: string
  nestedLabel: string
  hideLabelProp: string
  descriptionProp: string
  fieldMaxItemsContent: string
}): string {
  return `${indent}<DynamicListField
${indent}  name={${pathExpression}}
${indent}  label="${nestedLabel}"${hideLabelProp}${descriptionProp}
${indent}  disabled={isPending}${fieldMaxItemsContent}
${indent}  placeholder="Enter value"
${indent}/>`
}

function renderNestedFieldFormFieldTemplate({
  indent,
  pathNameProp,
  formItemProps,
  labelWithDescription
}: {
  indent: string
  pathNameProp: string
  formItemProps: string
  labelWithDescription: string
}): string {
  return `${indent}<FormField
${indent}  control={form.control}
${indent}  name=${pathNameProp}
${indent}  render={({ field: formField }) => (
${indent}    <FormItem${formItemProps}>
${indent}      <FormControl>
${indent}        <Checkbox checked={formField.value} onCheckedChange={formField.onChange} disabled={isPending} />
${indent}      </FormControl>
${indent}      ${labelWithDescription}
${indent}    </FormItem>
${indent}  )}
${indent}/>`
}

function renderNestedFieldFormFieldDetailsTemplate({
  indent,
  pathNameProp,
  formItemProps,
  labelWithDescription,
  acceptProp
}: {
  indent: string
  pathNameProp: string
  formItemProps: string
  labelWithDescription: string
  acceptProp: string
}): string {
  return `${indent}<FormField
${indent}  control={form.control}
${indent}  name=${pathNameProp}
${indent}  render={({ field: formField }) => (
${indent}    <FormItem${formItemProps}>
${indent}      ${labelWithDescription}
${indent}      <FormControl>
${indent}        <MediaField value={formField.value} onChange={formField.onChange} onBlur={formField.onBlur} disabled={isPending}${acceptProp} />
${indent}      </FormControl>
${indent}      <FormMessage />
${indent}    </FormItem>
${indent}  )}
${indent}/>`
}

function renderNestedFieldFormFieldDetailsBranchTemplate({
  indent,
  pathNameProp,
  formItemProps,
  labelWithDescription,
  toLowerCase
}: {
  indent: string
  pathNameProp: string
  formItemProps: string
  labelWithDescription: string
  toLowerCase: string
}): string {
  return `${indent}<FormField
${indent}  control={form.control}
${indent}  name=${pathNameProp}
${indent}  render={({ field: formField }) => (
${indent}    <FormItem${formItemProps}>
${indent}      ${labelWithDescription}
${indent}      <FormControl>
${indent}        <IconPicker value={formField.value} onValueChange={formField.onChange} triggerPlaceholder="Select ${toLowerCase}" />
${indent}      </FormControl>
${indent}      <FormMessage />
${indent}    </FormItem>
${indent}  )}
${indent}/>`
}

function renderNestedFieldFormFieldDetailsBranchBodyTemplate({
  indent,
  pathNameProp,
  formItemProps,
  labelWithDescription,
  stepProp,
  toLowerCase,
  emptyValue
}: {
  indent: string
  pathNameProp: string
  formItemProps: string
  labelWithDescription: string
  stepProp: string
  toLowerCase: string
  emptyValue: string
}): string {
  return `${indent}<FormField
${indent}  control={form.control}
${indent}  name=${pathNameProp}
${indent}  render={({ field: formField }) => (
${indent}    <FormItem${formItemProps}>
${indent}      ${labelWithDescription}
${indent}      <FormControl>
${indent}        <Input
${indent}          type="number"${stepProp}
${indent}          placeholder="Enter ${toLowerCase}"
${indent}          disabled={isPending}
${indent}          value={formField.value ?? ''}
${indent}          onChange={(event) => {
${indent}          const nextValue = event.target.value === '' ? ${emptyValue} : event.target.valueAsNumber
${indent}          const normalizedValue = Number.isNaN(nextValue) ? ${emptyValue} : nextValue
${indent}          formField.onChange(normalizedValue)
${indent}          }}
${indent}          onBlur={formField.onBlur}
${indent}          name={formField.name}
${indent}          ref={formField.ref}
${indent}        />
${indent}      </FormControl>
${indent}      <FormMessage />
${indent}    </FormItem>
${indent}  )}
${indent}/>`
}

export function renderNestedFieldRenderer(
  request:
    | {
        kind: 'renderNestedFieldLayoutTemplate'
        props: {
          heading: string
          indent: string
          columnsGrid: string
          groupFields: string
        }
      }
    | {
        kind: 'renderNestedFieldDynamicListFieldTemplate'
        props: {
          indent: string
          pathExpression: string
          nestedLabel: string
          hideLabelProp: string
          descriptionProp: string
          fieldMaxItemsContent: string
        }
      }
    | {
        kind: 'renderNestedFieldFormFieldTemplate'
        props: {
          indent: string
          pathNameProp: string
          formItemProps: string
          labelWithDescription: string
        }
      }
    | {
        kind: 'renderNestedFieldFormFieldDetailsTemplate'
        props: {
          indent: string
          pathNameProp: string
          formItemProps: string
          labelWithDescription: string
          acceptProp: string
        }
      }
    | {
        kind: 'renderNestedFieldFormFieldDetailsBranchTemplate'
        props: {
          indent: string
          pathNameProp: string
          formItemProps: string
          labelWithDescription: string
          toLowerCase: string
        }
      }
    | {
        kind: 'renderNestedFieldFormFieldDetailsBranchBodyTemplate'
        props: {
          indent: string
          pathNameProp: string
          formItemProps: string
          labelWithDescription: string
          stepProp: string
          toLowerCase: string
          emptyValue: string
        }
      }
): string {
  switch (request.kind) {
    case 'renderNestedFieldLayoutTemplate':
      return renderNestedFieldLayoutTemplate(request.props)
    case 'renderNestedFieldDynamicListFieldTemplate':
      return renderNestedFieldDynamicListFieldTemplate(request.props)
    case 'renderNestedFieldFormFieldTemplate':
      return renderNestedFieldFormFieldTemplate(request.props)
    case 'renderNestedFieldFormFieldDetailsTemplate':
      return renderNestedFieldFormFieldDetailsTemplate(request.props)
    case 'renderNestedFieldFormFieldDetailsBranchTemplate':
      return renderNestedFieldFormFieldDetailsBranchTemplate(request.props)
    case 'renderNestedFieldFormFieldDetailsBranchBodyTemplate':
      return renderNestedFieldFormFieldDetailsBranchBodyTemplate(request.props)
  }

  throw new Error('Unsupported renderer request')
}
